'use client';
import DataTable, { TableColumn } from '@/components/DataTable';
import { OtnGameOtnTeamPeriod } from '@/data/models/ot_nationals/otn_game_otn_team_period';
import { useGameTeamPeriodsLiveScores } from '@/hooks/useGameUpdates';
import { OtnGame } from '@models/ot_nationals/otn_game';

type BoxScoreRow = {
	teamLabel: string;
	q1: number;
	q2: number;
	q3: number;
	q4: number;
	total: number;
};

const getQuarterPoints = (periods: OtnGameOtnTeamPeriod[] | undefined, quarter: number): number => {
	if (!periods || !periods.length) return 0;

	const match = periods.find((p) => p.period_id === quarter);
	return match?.points ?? 0;
};

const buildRow = (team: any | undefined, periods: OtnGameOtnTeamPeriod[] | undefined): BoxScoreRow | null => {
	if (!team) return null;

	const q1 = getQuarterPoints(periods, 1);
	const q2 = getQuarterPoints(periods, 2);
	const q3 = getQuarterPoints(periods, 3);
	const q4 = getQuarterPoints(periods, 4);
	const total = q1 + q2 + q3 + q4;

	const teamLabel = team.otn_team?.team_code;

	return {
		teamLabel,
		q1,
		q2,
		q3,
		q4,
		total,
	};
};

const BoxScore = ({ game }: { game: OtnGame }) => {
	// live + initial DB snapshot
	const { homeTeam, awayTeam, homePeriods, awayPeriods } = useGameTeamPeriodsLiveScores(game);

	// use periods from the hook, not from game
	const homeRow = buildRow(homeTeam, homePeriods);
	const awayRow = buildRow(awayTeam, awayPeriods);

	const rows: BoxScoreRow[] = [homeRow, awayRow].filter(Boolean) as BoxScoreRow[];

	if (!rows.length) return null;

	const valueCellClass = 'text-white font-roboto-flex text-[13px] not-italic font-[400] leading-[150%]';
	const headerClass = 'text-white font-roboto-flex text-[13px] not-italic font-[773] leading-[150%]';
	const teamCellClass =
		'text-white overflow-hidden text-ellipsis whitespace-nowrap font-roboto-flex text-[13px] not-italic font-[400] leading-[150%]';

	const columns: TableColumn<BoxScoreRow>[] = [
		{
			key: 'team',
			header: 'TEAM',
			align: 'left',
			colClassName: 'w-[120px] md:w-[160px]',
			headerClassName: headerClass + ' pl-4',
			cellClassName: teamCellClass + ' pl-4',
			render: (row) => row.teamLabel.toUpperCase() || '--',
		},
		{
			key: 'q1',
			header: 'Q1',
			align: 'center',
			headerClassName: headerClass,
			cellClassName: valueCellClass,
			render: (r) => r.q1,
		},
		{
			key: 'q2',
			header: 'Q2',
			align: 'center',
			headerClassName: headerClass,
			cellClassName: valueCellClass,
			render: (r) => r.q2,
		},
		{
			key: 'q3',
			header: 'Q3',
			align: 'center',
			headerClassName: headerClass,
			cellClassName: valueCellClass,
			render: (r) => r.q3,
		},
		{
			key: 'q4',
			header: 'Q4',
			align: 'center',
			headerClassName: headerClass,
			cellClassName: valueCellClass,
			render: (r) => r.q4,
		},
		{
			key: 'total',
			header: 'TOTAL',
			align: 'center',
			headerClassName: headerClass,
			cellClassName: valueCellClass,
			render: (r) => r.total,
		},
	];

	return (
		<section className="otn-section-border flex flex-col gap-6 bg-black px-6 py-6">
			<div className="border-b-[2px] border-b-[#333] pb-6 md:px-0">
				<h2 className="otn-heading">Box Score</h2>
			</div>

			<DataTable columns={columns} rows={rows} getRowKey={(row, index) => `${row.teamLabel || 'team'}-${index}`} />
		</section>
	);
};

export default BoxScore;
